/**
 * 生图记录（Alice 页面 images 的复刻）：成本统计与生成画廊两个标签，共用日期范围。
 *
 * 背景：数据来自网关库 usage_records 里 endpoint 为生图的记录；成本是上游成本（采购价口径），
 *   没有采购价的渠道成本为空，页面如实显示「未知」，不会用用户实扣冒充成本。
 * 设计意图：
 *   - 两个标签各自加载（切到才请求），日期范围共用；统计按浏览器时区的自然日划分（tz_offset），
 *     画廊的起止则转成 UTC ISO。
 *   - 画廊预览走同源的 image-proxy，只有正文仍在保存期内的记录才有图，其余显示占位并说明原因。
 *   - 导出要管理员并写审计，非管理员按钮置灰。
 * 关键约束：筛选与翻页的慢响应用序号丢弃；画廊图片用 loading=lazy，避免一页 24 张同时抢带宽。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CostQuote, ImageCostStats, ImageLogs, ImageQuery, ImageRecord } from '@ap/api-client/admin-runtime'
import { imagePreviewUrl, runtimeApi, saveBlob } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { Link, fmtDateTime, useToast } from '@ap/ui'
import { useAdmin } from '../../session.tsx'
import { OpsNote, OpsPage, Stat, Stats } from '../../alice/kit.tsx'
import { ExportDialog, SearchInput, dayEndIso, dayStartIso, fmtInt, fmtPct, localOffsetMinutes, useMoney, type ExportParams } from '../../alice/kit-runtime.tsx'
import { ToolSelect, ellipsis, fieldStyle, subColor } from '../../alice/kit-runtime2.tsx'
import { ImageDetailModal } from './runtime/ImageDetailModal.tsx'

const PAGE = 24

interface Filters {
  user: string
  model: string
  prompt: string
  status: 'all' | 'success' | 'failed'
}
const NO_FILTERS: Filters = { user: '', model: '', prompt: '', status: 'all' }

export function Images() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const money = useMoney()
  const [tab, setTab] = useState<'stats' | 'gallery'>('stats')
  const [draftStart, setDraftStart] = useState('')
  const [draftEnd, setDraftEnd] = useState('')
  const [range, setRange] = useState({ start: '', end: '' })
  const [draft, setDraft] = useState<Filters>(NO_FILTERS)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [offset, setOffset] = useState(0)

  const [stats, setStats] = useState<ImageCostStats | null>(null)
  const [statsError, setStatsError] = useState('')
  const [statsLoading, setStatsLoading] = useState(false)
  const [gallery, setGallery] = useState<ImageLogs | null>(null)
  const [galleryError, setGalleryError] = useState('')
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [detail, setDetail] = useState<ImageRecord | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const statsSeq = useRef(0)
  const gallerySeq = useRef(0)

  const query = useCallback(
    (): ImageQuery => ({
      user_id: filters.user.trim() || undefined,
      model: filters.model.trim() || undefined,
      prompt: filters.prompt.trim() || undefined,
      status: filters.status,
      start_date: dayStartIso(range.start),
      end_date: dayEndIso(range.end),
    }),
    [filters, range],
  )

  const loadStats = useCallback(async () => {
    const my = ++statsSeq.current
    setStatsLoading(true)
    try {
      const d = await runtimeApi.imageCostStats({ start_date: range.start, end_date: range.end, tz_offset: localOffsetMinutes() })
      if (my !== statsSeq.current) return
      setStats(d)
      setStatsError('')
    } catch (e) {
      console.error('[images] 成本统计加载失败', e)
      if (my === statsSeq.current) setStatsError(errorTextRuntime(e, locale))
    } finally {
      if (my === statsSeq.current) setStatsLoading(false)
    }
  }, [range, locale])

  const loadGallery = useCallback(async () => {
    const my = ++gallerySeq.current
    setGalleryLoading(true)
    try {
      const d = await runtimeApi.imageLogs({ ...query(), limit: PAGE, offset })
      if (my !== gallerySeq.current) return
      setGallery(d)
      setGalleryError('')
    } catch (e) {
      console.error('[images] 画廊加载失败', e)
      if (my === gallerySeq.current) setGalleryError(errorTextRuntime(e, locale))
    } finally {
      if (my === gallerySeq.current) setGalleryLoading(false)
    }
  }, [query, offset, locale])

  useEffect(() => {
    if (tab === 'stats') void loadStats()
  }, [tab, loadStats])
  useEffect(() => {
    if (tab === 'gallery') void loadGallery()
  }, [tab, loadGallery])

  const applyRange = (start: string, end: string) => {
    if (start && end && start > end) return toast.error(t(G.exportDialog.badRange))
    setDraftStart(start)
    setDraftEnd(end)
    setOffset(0)
    setRange({ start, end })
  }
  const applyFilters = (f: Filters) => {
    setDraft(f)
    setOffset(0)
    setFilters(f)
  }

  const doExport = async (p: ExportParams) => {
    const r = await runtimeApi.exportImages({ ...query(), start_date: dayStartIso(p.startDate), end_date: dayEndIso(p.endDate), limit: p.maxRows })
    saveBlob(r.blob, r.info.filename)
    setExportOpen(false)
    if (r.info.truncated) toast.error(t(G.exportDialog.truncated, { rows: fmtInt(r.info.rows), matched: fmtInt(r.info.matched) }))
    else toast.ok(t(G.exportDialog.done))
  }

  const sum = stats?.summary
  const total = gallery?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const page = Math.floor(offset / PAGE) + 1

  return (
    <OpsPage eyebrow={t(G.images.eyebrow)} title={t(G.images.title)} desc={t(G.images.desc)} chip={t(G.images.chip)}>
      <div className="ops-toolbar">
        <input type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} style={fieldStyle} />
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t(G.common.to)}</span>
        <input type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} style={fieldStyle} />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => applyRange(draftStart, draftEnd)}>
          {t(G.images.refreshDates)}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => applyRange('', '')}>
          {t(G.images.clearDates)}
        </button>
      </div>

      <div className="img-tabs" role="tablist" aria-label={t(G.images.tabsAria)}>
        <button type="button" className={tab === 'stats' ? 'active' : ''} onClick={() => setTab('stats')}>
          {t(G.images.tabStats)}
        </button>
        <button type="button" className={tab === 'gallery' ? 'active' : ''} onClick={() => setTab('gallery')}>
          {t(G.images.tabGallery)}
        </button>
      </div>

      {tab === 'stats' ? (
        <>
          <Stats>
            <Stat label={t(G.images.stImages)} value={sum ? fmtInt(sum.images) : '—'} sub={t(G.images.stRequests, { n: fmtInt(sum?.requests) })} />
            <Stat
              label={t(G.images.stUpstream)}
              value={sum ? money.usd(sum.upstream_cost, 4) : '—'}
              sub={t(G.images.stCoverage, { p: fmtPct(sum?.cost_coverage), n: fmtInt(sum?.known_cost_images) })}
            />
            <Stat
              label={t(G.images.stMargin)}
              value={sum ? money.usd(sum.known_margin, 4) : '—'}
              color={sum && sum.known_margin < 0 ? 'error' : undefined}
              sub={t(G.images.stCharge, { v: money.petals(sum?.known_user_cost ?? 0) })}
            />
            <Stat
              label={t(G.images.stFallbacks)}
              value={sum ? fmtInt(sum.fallback_successes) : '—'}
              sub={t(G.images.stFailures, { r: fmtPct(sum?.fallback_rate), n: fmtInt(sum?.all_failed) })}
            />
          </Stats>
          <OpsNote>{t(G.images.note)}</OpsNote>
          <div className="ops-section-card" style={{ overflow: 'hidden' }}>
            <div className="ops-section-head">
              <div>
                <div className="ops-section-title">{t(G.images.dailyTitle)}</div>
                <div className="ops-section-copy">{stats ? t(G.images.dailyRange, { start: stats.start_date, end: stats.end_date }) : t(G.common.loading)}</div>
              </div>
              <button type="button" className="btn btn-secondary btn-sm" disabled={statsLoading} onClick={() => void loadStats()}>
                {t(G.images.refreshStats)}
              </button>
            </div>
            {statsError && !stats ? (
              <div className="empty">{t(G.images.statsFailed, { msg: statsError })}</div>
            ) : (
              <div className="image-cost-tables">
                <div className="table-inner">
                  <table>
                    <thead>
                      <tr>
                        <th>{t(G.images.cDate)}</th>
                        <th>{t(G.images.cImagesRequests)}</th>
                        <th>{t(G.images.cCoverage)}</th>
                        <th>{t(G.images.cUpstream)}</th>
                        <th>{t(G.images.cMargin)}</th>
                        <th>{t(G.images.cFallback)}</th>
                        <th>{t(G.images.cAllFailed)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!stats ? (
                        <tr><td colSpan={7} className="empty">{t(G.common.loading)}</td></tr>
                      ) : stats.daily.length === 0 ? (
                        <tr><td colSpan={7} className="empty">{t(G.images.emptyDaily)}</td></tr>
                      ) : (
                        [...stats.daily].reverse().map((d) => (
                          <tr key={d.date}>
                            <td className="mono" style={{ fontSize: 11 }}>{d.date}</td>
                            <td>{fmtInt(d.images)} / {fmtInt(d.requests)}</td>
                            <td>{fmtPct(d.cost_coverage)}</td>
                            <td className="mono" style={{ fontSize: 11 }}>{d.known_cost_images > 0 ? money.usd(d.upstream_cost, 4) : '—'}</td>
                            <td className="mono" style={{ fontSize: 11, ...subColor(d.known_margin < 0) }}>{d.known_cost_images > 0 ? money.usd(d.known_margin, 4) : '—'}</td>
                            <td>{fmtInt(d.fallback_successes)}</td>
                            <td style={d.all_failed > 0 ? { color: 'var(--error-text)' } : undefined}>{fmtInt(d.all_failed)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="table-inner">
                  <table>
                    <thead>
                      <tr>
                        <th>{t(G.images.cProvider)}</th>
                        <th>{t(G.images.cImages)}</th>
                        <th>{t(G.images.cQuote)}</th>
                        <th>{t(G.images.cPerImage)}</th>
                        <th>{t(G.images.cKnownCost)}</th>
                        <th>{t(G.images.cTakeover)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!stats ? (
                        <tr><td colSpan={6} className="empty">{t(G.common.loading)}</td></tr>
                      ) : stats.providers.length === 0 ? (
                        <tr><td colSpan={6} className="empty">{t(G.images.emptyProviders)}</td></tr>
                      ) : (
                        stats.providers.map((p) => {
                          const known = p.known_cost_images > 0
                          const title = known
                            ? t(G.images.costTitleKnown, { c: fmtPct(p.cost_coverage), m: money.usd(p.known_margin, 4) })
                            : p.cost_quote?.maintained
                              ? t(G.images.costTitleQuoted)
                              : t(G.images.costTitleMissing)
                          return (
                            <tr key={`${p.channel_id}/${p.upstream_model}/${p.model}`}>
                              <td>
                                <div className="mono" style={{ fontSize: 11, ...ellipsis, maxWidth: 200 }} title={`${p.channel_id}/${p.upstream_model}`}>
                                  {p.channel_id}/{p.upstream_model}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{p.model}</div>
                              </td>
                              <td>{fmtInt(p.images)}</td>
                              <td><QuoteCell q={p.cost_quote} /></td>
                              <td className="mono" style={{ fontSize: 11 }} title={title}>
                                {known && p.avg_upstream_cost_per_image !== null ? money.usd(p.avg_upstream_cost_per_image, 6) : p.cost_quote?.maintained ? t(G.images.notRecorded) : t(G.images.unknown)}
                              </td>
                              <td className="mono" style={{ fontSize: 11 }} title={title}>
                                {known ? money.usd(p.upstream_cost, 4) : '—'}
                                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t(G.images.coverageOf, { p: fmtPct(p.cost_coverage) })}</div>
                              </td>
                              <td>{fmtInt(p.fallback_successes)}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="ops-toolbar">
            <SearchInput width={200} placeholder={t(G.images.phUser)} value={draft.user} onChange={(e) => setDraft({ ...draft, user: e.target.value })} onEnter={() => applyFilters(draft)} />
            <SearchInput width={150} placeholder={t(G.images.phModel)} value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} onEnter={() => applyFilters(draft)} />
            <SearchInput width={180} placeholder={t(G.images.phPrompt)} value={draft.prompt} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} onEnter={() => applyFilters(draft)} />
            <ToolSelect
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as Filters['status'] })}
              options={[
                { value: 'all', label: t(G.images.stAll) },
                { value: 'success', label: t(G.images.stSuccess) },
                { value: 'failed', label: t(G.images.stFailed) },
              ]}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={() => applyFilters(draft)}>
              {t(G.common.filter)}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => applyFilters(NO_FILTERS)}>
              {t(G.images.clearFilters)}
            </button>
            <span className="ops-toolbar-count">{t(G.images.totalBadge, { n: fmtInt(total) })}</span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!isAdmin} title={isAdmin ? t(G.images.exportTip) : t(G.common.adminOnly)} onClick={() => setExportOpen(true)}>
              {t(G.images.exportJsonl)}
            </button>
          </div>

          <div className="ops-section-card">
            <div className="ops-section-head">
              <div>
                <div className="ops-section-title">{t(G.images.galleryTitle)}</div>
                <div className="ops-section-copy">{t(G.images.galleryCopy)}</div>
              </div>
            </div>
            {galleryError && !gallery ? (
              <div className="empty">
                {t(G.images.galleryFailed)}
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => void loadGallery()}>
                    {t(G.images.reload)}
                  </button>
                </div>
              </div>
            ) : galleryLoading && !gallery ? (
              <div className="empty">{t(G.common.loading)}</div>
            ) : (gallery?.records.length ?? 0) === 0 ? (
              <div className="empty">{t(G.images.emptyGallery)}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, padding: 14 }}>
                {gallery?.records.map((r) => (
                  <GalleryCard key={r.request_id} r={r} locale={locale} onOpen={() => setDetail(r)} />
                ))}
              </div>
            )}
            {total > PAGE && (
              <div style={{ padding: '12px 16px', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginRight: 6 }}>{t(G.common.pageOf, { p: page, n: pages })}</span>
                <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setOffset((page - 2) * PAGE)}>
                  {t(G.common.prev)}
                </button>
                {pageWindow(page, pages).map((p) => (
                  <button key={p} type="button" className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setOffset((p - 1) * PAGE)}>
                    {p}
                  </button>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" disabled={page >= pages} onClick={() => setOffset(page * PAGE)}>
                  {t(G.common.next)}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <ImageDetailModal record={detail} onClose={() => setDetail(null)} />
      <ExportDialog
        open={exportOpen}
        title={t(G.exportDialog.imagesTitle)}
        note={t(G.exportDialog.current)}
        onSubmit={async (p) => {
          try {
            await doExport(p)
          } catch (e) {
            console.error('[images] 导出失败', e)
            throw e
          }
        }}
        onClose={() => setExportOpen(false)}
      />
    </OpsPage>
  )
}

/** 当前页前后各两页，首尾页始终保留。 */
function pageWindow(page: number, pages: number): number[] {
  const set = new Set<number>([1, pages])
  for (let p = page - 2; p <= page + 2; p++) if (p >= 1 && p <= pages) set.add(p)
  return [...set].sort((a, b) => a - b)
}

function QuoteCell({ q }: { q: CostQuote | null }) {
  const { t } = useI18n()
  if (!q || !q.maintained) {
    return (
      <>
        <span style={{ color: 'var(--text-tertiary)' }}>{t(G.images.notConfigured)}</span>
        <div style={{ fontSize: 10 }}>
          <Link href="/channel-models">{t(G.images.register)}</Link>
        </div>
      </>
    )
  }
  const sym = q.currency === 'CNY' ? '¥' : '$'
  const main =
    q.discount === '0'
      ? t(G.images.freeDeal)
      : q.per_image
        ? t(G.images.perImage, { v: `${sym}${q.per_image}` })
        : q.prompt_per_1m || q.completion_per_1m
          ? t(G.images.tokenPrice, { i: `${sym}${q.prompt_per_1m ?? '—'}`, o: `${sym}${q.completion_per_1m ?? '—'}` })
          : t(G.images.onlyOther)
  const notes = [q.discount !== '1' && q.discount !== '0' ? t(G.images.discountNote, { d: q.discount }) : '', q.currency === 'CNY' ? t(G.images.cnyNote) : '', q.enabled ? '' : t(G.images.disabledNote)].filter(Boolean)
  return (
    <>
      <span className="mono" style={{ fontSize: 11 }}>{main}</span>
      {notes.length > 0 && <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{notes.join(' · ')}</div>}
    </>
  )
}

function GalleryCard({ r, locale, onOpen }: { r: ImageRecord; locale: 'ko' | 'zh' | 'en'; onOpen: () => void }) {
  const { t } = useI18n()
  const money = useMoney()
  const [broken, setBroken] = useState(false)
  const failed = r.status === 'failed'
  const showImage = !failed && r.preview_count > 0 && !broken
  return (
    <div className="image-record-card" style={{ cursor: 'pointer', border: '1px solid var(--border-medium)', background: 'var(--bg-card)', overflow: 'hidden', minWidth: 0 }} onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <div style={{ aspectRatio: '1 / 1', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {showImage ? (
          <img src={imagePreviewUrl(r.request_id, 0)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setBroken(true)} />
        ) : failed ? (
          <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: 'var(--error-text)' }}>
            <b>{t(G.images.failedShort)}</b>
            <div>{t(G.images.httpOf, { n: r.status_code })}</div>
            <div style={{ marginTop: 4, ...ellipsis, maxWidth: 160 }} title={r.error_message}>{r.error_message}</div>
          </div>
        ) : (
          <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {broken ? t(G.images.previewFailed) : t(G.images.noPreview)}
            {!broken && <div style={{ fontSize: 10 }}>{t(G.images.noPreviewWhy)}</div>}
          </div>
        )}
      </div>
      <div style={{ padding: '8px 10px', fontSize: 11 }}>
        <div style={{ fontSize: 12, color: 'var(--text-primary)', ...ellipsis }} title={r.prompt}>
          {r.prompt || t(G.images.noPrompt)}
        </div>
        <div className="mono" style={{ color: 'var(--text-tertiary)', ...ellipsis }} title={r.model}>{r.shell_display_name || r.model}</div>
        <div style={{ color: 'var(--text-tertiary)', ...ellipsis }}>{r.account_name || r.account_id.slice(0, 8)} · {fmtDateTime(r.created_at, locale)}</div>
        <div style={{ color: 'var(--text-secondary)' }}>
          {money.petals(r.cost)}
          {!failed && ` · ${r.upstream_cost === null || r.images <= 0 ? t(G.images.costUnknown) : t(G.images.costPerImage, { v: money.usd(Math.floor(r.upstream_cost / r.images), 4) })}`}
        </div>
      </div>
    </div>
  )
}
